import React, { useEffect, useState, useMemo } from 'react';
import { Plan } from '../core/_models';
import { getPlans } from '../core/subscriptionService';
import { PlanForm } from './PlanForm';
import { ModalDeletePlan } from './ModalDeletePlan';
import Select from 'react-select';
import { getReactSelectStyles } from '../core/reactSelectTheme';
import { useThemeMode } from '../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

const featureOptions = [
  { value: 'all', label: 'Todos os Itens' },
  { value: 'monitoramento', label: 'Monitoramento 24/7' },
  { value: 'manutencao', label: 'Manutenção Preventiva' },
  { value: 'limpeza', label: 'Limpeza de Painéis' },
];

export const PlansTab: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Plan | null>(null);
  const { mode } = useThemeMode();
  const selectStyles = useMemo(() => getReactSelectStyles(), [mode]);

  // Filters state
  const [searchName, setSearchName] = useState('');
  const [searchCode, setSearchCode] = useState('');
  const [selectedFeature, setSelectedFeature] = useState(featureOptions[0]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const loadPlans = () => {
    setLoading(true);
    getPlans().then((data) => {
      setPlans(data);
      setLoading(false);
    }).catch(() => {
      setPlans([]);
      setLoading(false);
    });
  };

  // Lógica de filtragem dos planos no frontend (Client-side)
  const filteredPlans = useMemo(() => {
    return plans.filter((plan) => {
      if (searchName) {
        if (!plan.name.toLowerCase().includes(searchName.toLowerCase())) {
          return false;
        }
      }

      if (searchCode) {
        if (!plan.code?.toLowerCase().includes(searchCode.toLowerCase())) {
          return false;
        }
      }

      if (selectedFeature.value !== 'all') {
        const hasFeature = plan.features?.some((feat) =>
          feat.toLowerCase().includes(selectedFeature.value.toLowerCase())
        );
        if (!hasFeature) {
          return false;
        }
      }

      return true;
    });
  }, [plans, searchName, searchCode, selectedFeature]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchName, searchCode, selectedFeature]);

  const paginatedPlans = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredPlans.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredPlans, currentPage]);

  useEffect(() => {
    loadPlans();
  }, []);

  const handleDelete = (plan: Plan) => {
    setDeleteTarget(plan);
  };

  return (
    <div className='row g-5 g-xl-8'>
      {showForm ? (
        <PlanForm 
          show={showForm} 
          handleClose={() => {
            setShowForm(false);
            loadPlans();
          }} 
          plan={editingPlan} 
        />
      ) : (
      <div className='col-xl-12'>
        <div className='card mb-5 mb-xl-8'>
          <div className='card-header border-0 pt-5 flex-column'>
            <div className='d-flex justify-content-between align-items-center w-100 mb-5'>
              <h3 className='card-title align-items-start flex-column m-0'>
                <span className='card-label fw-bolder fs-3 mb-1'>Planos Disponíveis</span>
                <span className='text-muted mt-1 fw-bold fs-7'>{filteredPlans.length} Planos Encontrados</span>
              </h3>
              <div className='card-toolbar'>
                <button 
                  type='button' 
                  className='btn btn-sm btn-primary'
                  onClick={() => {
                    setEditingPlan(null);
                    setShowForm(true);
                  }}
                >
                  <i className='bi bi-plus-lg me-1'></i> Novo Plano
                </button>
              </div>
            </div>

            {/* Barra de Filtros */}
            <div className='row g-4 mb-4 w-100'>
              <div className='col-lg-4'>
                <label className='form-label fs-7 fw-bolder text-gray-700'>Nome do Plano</label>
                <div className='position-relative'>
                  <i className='bi bi-search position-absolute top-50 translate-middle-y ms-4 text-gray-500'></i>
                  <input
                    type='text'
                    className='form-control form-control-solid ps-12'
                    placeholder='Ex: Solar Premium'
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                  />
                </div>
              </div>
              <div className='col-lg-4'>
                <label className='form-label fs-7 fw-bolder text-gray-700'>Código</label>
                <div className='position-relative'>
                  <i className='bi bi-upc-scan position-absolute top-50 translate-middle-y ms-4 text-gray-500'></i>
                  <input
                    type='text'
                    className='form-control form-control-solid ps-12'
                    placeholder='Ex: PLAN-01'
                    value={searchCode}
                    onChange={(e) => setSearchCode(e.target.value)}
                  />
                </div>
              </div>
              <div className='col-lg-4'>
                <label className='form-label fs-7 fw-bolder text-gray-700'>Itens Inclusos</label>
                <Select
                  options={featureOptions}
                  value={selectedFeature}
                  onChange={(val) => setSelectedFeature(val as any)}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Selecionar item..."
                  isSearchable
                  styles={selectStyles}
                />
              </div>
            </div>
          </div>
          <div className='card-body py-3'>
            {loading ? (
              <div className='d-flex justify-content-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : filteredPlans.length === 0 ? (
              <div className='d-flex flex-column align-items-center justify-content-center py-15'>
                <i className='bi bi-clipboard-data fs-2tx text-gray-300 mb-5'></i>
                <span className='text-gray-500 fw-bold fs-5'>Nenhum plano encontrado.</span>
                <span className='text-muted fs-7 mt-2'>Ajuste os filtros ou crie um novo plano.</span>
              </div>
            ) : (
              <div className='d-flex flex-column gap-5'>
                {paginatedPlans.map((plan) => (
                  <div key={plan.id} className='border border-dashed border-gray-300 rounded p-7'>
                    <div className='d-flex align-items-center justify-content-between'>
                      <div className='d-flex align-items-center gap-4'>
                        <div className='symbol symbol-50px symbol-light-primary'>
                          <span className='symbol-label'>
                            <i className='bi bi-award fs-2x text-primary'></i>
                          </span>
                        </div>
                        <div className='d-flex flex-column'>
                          <div className='d-flex align-items-center gap-2'>
                            <span className='text-dark text-hover-primary fs-4 fw-bolder cursor-pointer'>
                              {plan.name}
                            </span>
                            <span className={`badge badge-light-${plan.status === 'ATIVO' ? 'success' : 'secondary'} fs-8`}>{plan.status || 'ATIVO'}</span>
                          </div>
                          <span className='text-muted fw-bold'>
                            {plan.periodicity || 'MENSAL'} • Taxa de adesão: R$ {(plan.adhesion_fee || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center gap-6'>
                        <div className='btn-group' role='group'>
                          <button 
                            type='button' 
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm' 
                            title='Editar Plano'
                            onClick={() => {
                              setEditingPlan(plan);
                              setShowForm(true);
                            }}
                          >
                            <i className='bi bi-pencil fs-4'></i>
                          </button>
                          {plan.status === 'ATIVO' || !plan.status ? (
                            <button 
                              type='button' 
                              className='btn btn-icon btn-bg-light btn-active-light-danger btn-sm' 
                              title='Desativar Plano'
                              onClick={() => handleDelete(plan)}
                            >
                              <i className='bi bi-toggle-on fs-2 text-success'></i>
                            </button>
                          ) : (
                            <button 
                              type='button' 
                              className='btn btn-icon btn-bg-light btn-active-light-success btn-sm' 
                              title='Reativar Plano'
                              onClick={() => handleDelete(plan)}
                            >
                              <i className='bi bi-toggle-off fs-2 text-muted'></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Paginação */}
          {!loading && filteredPlans.length > 0 && (
            <div className='card-footer border-0 pt-0 pb-5 d-flex justify-content-between align-items-center'>
              <div className='text-gray-500 fw-bold fs-6'>
                Mostrando {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredPlans.length)} de {filteredPlans.length} planos
              </div>
              <div className='d-flex align-items-center gap-2'>
                <button 
                  className={`btn btn-light btn-sm btn-active-light-primary fw-bolder ${currentPage === 1 ? 'disabled' : ''}`}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                  disabled={currentPage === 1}
                >
                  <i className='bi bi-chevron-left'></i> Anterior
                </button>
                <button 
                  className={`btn btn-light btn-sm btn-active-light-primary fw-bolder ${currentPage * itemsPerPage >= filteredPlans.length ? 'disabled' : ''}`}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage * itemsPerPage >= filteredPlans.length}
                >
                  Próxima <i className='bi bi-chevron-right'></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      )}
      <ModalDeletePlan 
        show={!!deleteTarget} 
        plan={deleteTarget} 
        handleClose={() => {
          setDeleteTarget(null);
          loadPlans();
        }} 
      />
    </div>
  );
};
